import React, { createContext, useState, useEffect, useContext, ReactNode } from 'react';
import api from '../services/api';

export interface Usuario {
    id: number;
    nome: string;
    email: string;
    funcao: string;
    avatar_path?: string | null;
}

interface AuthContextData {
    signed: boolean;
    user: Usuario | null;
    loading: boolean;
    signIn: (email: string, senha_hash: string) => Promise<void>;
    signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextData>({} as AuthContextData);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [user, setUser] = useState<Usuario | null>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        // Ao iniciar o app, validar se o cookie da sessao ainda é válido chamando o /profile
        async function loadStoragedData() {
            try {
                const response = await api.get('/profile');
                if (response.data && response.data.id) {
                    setUser(response.data);
                }
            } catch (error) {
                setUser(null);
            } finally {
                setLoading(false);
            }
        }

        loadStoragedData();
    }, []);

    async function signIn(email: string, senha_hash: string) {
        try {
            const response = await api.post('/auth/login', { email, password: senha_hash });
            setUser(response.data.user);
        } catch (error: any) {
            if (error.response?.data?.error || error.response?.data?.message) {
                throw new Error(error.response.data.error || error.response.data.message);
            } else {
                throw new Error('Erro de instabilidade. O Backend pode estar indisponível.');
            }
        }
    }

    async function signOut() {
        try {
            await api.post('/auth/logout');
        } catch (err) {
            console.error(err);
        } finally {
            setUser(null);
        }
    }

    return (
        <AuthContext.Provider value={{ signed: !!user, user, loading, signIn, signOut }}>
            {children}
        </AuthContext.Provider>
    );
};

export function useAuth() {
    const context = useContext(AuthContext);
    return context;
}
